import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Fab,
  IconButton,
  Portal,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import { useMediaQuery, useTheme } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import {
  addEmployeeWorkHours,
  executeEmployeeDeactivation,
  fetchAllEmployees,
  fetchEmployeeById,
  fetchServices,
  executeEmployeeDeletion,
  EmployeeDeactivationPreview,
  EmployeeDeletionPreview,
  generateRecurringBreakBlockersForEmployee,
  getCurrentBusinessConfig,
  getEmployeeWorkHours,
  isRecurringBreakPartialFailureError,
  previewEmployeeDeactivation,
  previewEmployeeDeletion,
  updateEmployee,
  updateEmployeeSchedulesForDates,
  type GenerateRecurringBreakBlockersResult,
} from '../../firebase/firebase-delegate';
import { Appointment, BitStringData, Employee, RecurringBreak, Service, WorkHours } from '../../types';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import { arrayRemove, arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { WEBSITE_ID } from '../../projectConfig';
import { BUFFER_PERIOD, DAYJS_DAYS_OF_WEEK, DAYS_OF_WEEK_PREFERRED_ORDER, EMPLOYEE_BIO_MAX_LENGTH } from '../../constants';
import { dayjs } from '../../utils/dayjs-config';
import {
  extractTimesFromBitString,
  generateScheduleBitString,
} from '../../time/timeUtils';
import ScheduleCard from '../../components/employee/services/ScheduleCard';
import ImageSelect from '../../components/global/images/ImageSelector';
import { EMPLOYEES_DB_REF } from '../../firebase/firebase-references';
import { hasDuplicateEmployeeEmail, normalizeEmail } from '../../utils/emailUtils';
import { convertBusinessHoursToWorkHours } from '../../utils/businessHours';
import { applyEmployeeSaveCache, reconcileEmployeeCachesFromSource } from '../../utils/cache-delegate';
import { resolveBookingUxConfig } from '../../utils/bookingUxConfig';
import { useAdminData } from '../../contexts/AdminDataContext';

const areWorkHoursEqual = (left: WorkHours, right: WorkHours): boolean =>
  DAYS_OF_WEEK_PREFERRED_ORDER.every((day) => left[day] === right[day]);

const parseFullName = (value: string): { firstName: string; lastName: string } => {
  const trimmed = value.trim();
  if (!trimmed) {
    return { firstName: '', lastName: '' };
  }
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
};

const recurringBreakSortKey = (definition: RecurringBreak): string => (
  [
    definition.id,
    definition.weekday,
    definition.startTime,
    definition.endTime,
    definition.repeatEveryXWeeks,
    definition.repeatUntilDate || '',
    definition.indefinite ? '1' : '0',
  ].join('|')
);

const areRecurringBreakSetsEqual = (
  left: RecurringBreak[],
  right: RecurringBreak[],
): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  const normalizedLeft = left.map(recurringBreakSortKey).sort();
  const normalizedRight = right.map(recurringBreakSortKey).sort();
  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
};

const CACHE_STALE_TIME_MS = 1000 * 60 * 5;
const MISSING_WORK_HOURS_TEMPLATE_ERROR = 'No schedule template found';
type WorkHoursTemplateStatus = 'idle' | 'loaded' | 'missing_template' | 'error';
type ManageSectionKey = 'profile' | 'services' | 'work_hours' | null;

const ManageEmployeesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { currentEmployeeQuery } = useAdminData();

  const employeesQuery = useQuery<Employee[]>({
    queryKey: ['allEmployees'],
    queryFn: () => fetchAllEmployees(),
    staleTime: CACHE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });
  const servicesQuery = useQuery<Service[]>({
    queryKey: ['services'],
    queryFn: () => fetchServices(),
    staleTime: CACHE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });
  const businessConfigQuery = useQuery({
    queryKey: ['businessConfig'],
    queryFn: () => getCurrentBusinessConfig(),
    staleTime: CACHE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });

  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [twitter, setTwitter] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [active, setActive] = useState(true);
  const [profilePictureImagePath, setProfilePictureImagePath] = useState<string | undefined>(undefined);
  const [profilePictureFile, setProfilePictureFile] = useState<File | undefined>(undefined);
  const [selectedServiceIds, setSelectedServiceIds] = useState<Set<string>>(new Set());
  const [expandedManageSection, setExpandedManageSection] = useState<ManageSectionKey>('profile');
  const [workHoursTemplateStatus, setWorkHoursTemplateStatus] = useState<WorkHoursTemplateStatus>('idle');
  const businessHoursWorkHours = useMemo(
    () => convertBusinessHoursToWorkHours(businessConfigQuery.data?.businessInfo?.businessHours),
    [businessConfigQuery.data?.businessInfo?.businessHours]
  );
  const [workHours, setWorkHours] = useState<WorkHours>(businessHoursWorkHours);
  const [recurringBreakTemplates, setRecurringBreakTemplates] = useState<Record<keyof WorkHours, RecurringBreak[]>>(() =>
    DAYJS_DAYS_OF_WEEK.reduce((acc, day) => {
      acc[day] = [];
      return acc;
    }, {} as Record<keyof WorkHours, RecurringBreak[]>)
  );
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [resultSeverity, setResultSeverity] = useState<'success' | 'warning' | 'error' | null>(null);
  const [saveSuccessDialogOpen, setSaveSuccessDialogOpen] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');
  const [deleteSuccessDialogOpen, setDeleteSuccessDialogOpen] = useState(false);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deletePreviewData, setDeletePreviewData] = useState<EmployeeDeletionPreview | null>(null);
  const [deletePreviewError, setDeletePreviewError] = useState<string | null>(null);
  const [deleteTargetEmployeeId, setDeleteTargetEmployeeId] = useState<string | null>(null);
  const [deleteTargetEmployeeName, setDeleteTargetEmployeeName] = useState('');
  const deletePreviewRequestIdRef = useRef(0);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivatePreviewData, setDeactivatePreviewData] = useState<EmployeeDeactivationPreview | null>(null);
  const [deactivatePreviewError, setDeactivatePreviewError] = useState<string | null>(null);
  const deactivatePreviewRequestIdRef = useRef(0);
  const previousDialogEmployeeIdRef = useRef<string | null>(null);
  const [needsRefreshOnDialogClose, setNeedsRefreshOnDialogClose] = useState(false);

  const templateEndDate = dayjs().add(3, 'month');

  const normalizeSocialHandle = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    const withoutProtocol = trimmed.replace(/^https?:\/\//i, '');
    const withoutDomain = withoutProtocol
      .replace(/^www\./i, '')
      .replace(/^instagram\.com\//i, '')
      .replace(/^x\.com\//i, '')
      .replace(/^twitter\.com\//i, '');
    return withoutDomain.replace(/^@+/, '').split('/')[0].split('?')[0].trim();
  };

  const employeeWorkHoursQuery = useQuery<WorkHours>({
    queryKey: ['manageEmployeeWorkHours', selectedEmployee?.id],
    queryFn: () => getEmployeeWorkHours(selectedEmployee!.id),
    enabled: Boolean(selectedEmployee?.id && openDialog),
    staleTime: CACHE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const isWorkHoursLoadingForSelection =
    Boolean(selectedEmployee?.id && openDialog) &&
    (employeeWorkHoursQuery.isPending || employeeWorkHoursQuery.isFetching);

  useEffect(() => {
    if (!selectedEmployee || !openDialog) return;

    const isDifferentEmployee = previousDialogEmployeeIdRef.current !== selectedEmployee.id;
    if (isDifferentEmployee) {
      setResultMessage(null);
      setResultSeverity(null);
    }
    previousDialogEmployeeIdRef.current = selectedEmployee.id;

    setFirstName(selectedEmployee.firstName || '');
    setLastName(selectedEmployee.lastName || '');
    setEmail(selectedEmployee.email || '');
    setPhone(selectedEmployee.phone || '');
    setBio((selectedEmployee.bio || '').slice(0, EMPLOYEE_BIO_MAX_LENGTH));
    setInstagram(normalizeSocialHandle(selectedEmployee.socials?.instagram || ''));
    setFacebook(selectedEmployee.socials?.facebook || '');
    setTwitter(normalizeSocialHandle(selectedEmployee.socials?.twitter || ''));
    setIsAdmin(
      selectedEmployee.id === (businessConfigQuery.data?.paymentInfo?.owner || '')
        ? true
        : (selectedEmployee.admin || false)
    );
    setActive(selectedEmployee.active !== false);
    setResultMessage(null);
    setResultSeverity(null);
    setDeleteDialogOpen(false);
    setDeleteConfirmInput('');
    setDeletePreviewData(null);
    setDeletePreviewError(null);
    setDeleteTargetEmployeeId(null);
    setDeleteTargetEmployeeName('');
    deletePreviewRequestIdRef.current += 1;
    setDeactivateDialogOpen(false);
    setDeactivatePreviewData(null);
    setDeactivatePreviewError(null);
    deactivatePreviewRequestIdRef.current += 1;
    setProfilePictureImagePath(selectedEmployee.profilePictureImagePath);
    setProfilePictureFile(undefined);
    setExpandedManageSection('profile');
    setWorkHoursTemplateStatus('idle');

    const assignedIds = (servicesQuery.data ?? [])
      .filter((service) => service.employees.includes(selectedEmployee.id))
      .map((service) => service.id);
    setSelectedServiceIds(new Set(assignedIds));

    const initialBreakTemplates = DAYJS_DAYS_OF_WEEK.reduce((acc, day, index) => {
      acc[day] = (selectedEmployee.recurringBreaks ?? [])
        .filter((definition) => definition.weekday === index)
        .map((definition) => ({ ...definition }));
      return acc;
    }, {} as Record<keyof WorkHours, RecurringBreak[]>);
    setRecurringBreakTemplates(initialBreakTemplates);
  }, [openDialog, selectedEmployee, servicesQuery.data, businessConfigQuery.data?.paymentInfo?.owner]);

  useEffect(() => {
    if (openDialog) return;

    previousDialogEmployeeIdRef.current = null;
    setResultMessage(null);
    setResultSeverity(null);
  }, [openDialog]);

  useEffect(() => {
    if (!selectedEmployee || !openDialog) return;

    if (employeeWorkHoursQuery.isPending || employeeWorkHoursQuery.isFetching) {
      setWorkHoursTemplateStatus('idle');
      return;
    }

    if (employeeWorkHoursQuery.data) {
      setWorkHours(employeeWorkHoursQuery.data);
      setWorkHoursTemplateStatus('loaded');
      return;
    }

    if (employeeWorkHoursQuery.isError) {
      const errorMessage = employeeWorkHoursQuery.error instanceof Error
        ? employeeWorkHoursQuery.error.message
        : '';
      if (errorMessage.includes(MISSING_WORK_HOURS_TEMPLATE_ERROR)) {
        setWorkHours(businessHoursWorkHours);
        setWorkHoursTemplateStatus('missing_template');
      } else {
        setWorkHoursTemplateStatus('error');
      }
    }
  }, [
    employeeWorkHoursQuery.data,
    employeeWorkHoursQuery.error,
    employeeWorkHoursQuery.isError,
    employeeWorkHoursQuery.isFetching,
    employeeWorkHoursQuery.isPending,
    openDialog,
    selectedEmployee,
    businessHoursWorkHours,
  ]);

  const flattenRecurringBreaks = () =>
    DAYJS_DAYS_OF_WEEK.flatMap((day, index) =>
      (recurringBreakTemplates[day] || []).map((definition) => ({
        ...definition,
        weekday: index,
      }))
    );

  const handleScheduleChange = (day: keyof WorkHours, updatedSchedule: BitStringData) => {
    setWorkHours((prev) => ({
      ...prev,
      [day]: generateScheduleBitString(updatedSchedule),
    }));
  };

  const handleRecurringBreaksChange = (day: keyof WorkHours, breaks: RecurringBreak[]) => {
    setRecurringBreakTemplates((prev) => ({
      ...prev,
      [day]: breaks,
    }));
  };

  const toggleService = (serviceId: string) => {
    setSelectedServiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  };

  const handleProfilePictureChange = (newPicture?: File) => {
    setProfilePictureFile(newPicture);
    if (!newPicture) {
      setProfilePictureImagePath(undefined);
      return;
    }
    setProfilePictureImagePath((previous) => {
      if (previous) {
        return previous;
      }
      if (!selectedEmployee) {
        return undefined;
      }
      return `${EMPLOYEES_DB_REF}${selectedEmployee.id}/profilePicture`;
    });
  };

  const saveMutation = useMutation({
    mutationFn: async (input?: { deactivationStrategy?: 'handoff' | 'cancel' }) => {
      if (!selectedEmployee) throw new Error('No employee selected.');
      if (isWorkHoursLoadingForSelection) {
        throw new Error('Work hours are still loading. Please wait a moment and try again.');
      }
      const normalizedEmail = normalizeEmail(email);
      const existingEmployees = employeesQuery.data ?? [];
      if (!isResourceMode) {
        const duplicateEmail = hasDuplicateEmployeeEmail(existingEmployees, normalizedEmail, selectedEmployee.id);
        if (duplicateEmail) {
          throw new Error('An employee already exists with this email.');
        }
      }
      if (!isResourceMode && selectedEmployee.isOrchestrated !== true) {
        const currentEmail = normalizeEmail(selectedEmployee.email || '');
        if (normalizedEmail !== currentEmail) {
          throw new Error('Email cannot be changed for staff members with login access.');
        }
      }

      const isDeactivatingNow = selectedEmployee.active !== false && active === false;
      if (!firstName.trim()) {
        throw new Error('First name is required.');
      }
      if (isDeactivatingNow && !input?.deactivationStrategy) {
        throw new Error('Please choose how to handle upcoming appointments before deactivating this employee.');
      }

      let deactivationResult: { reassignedCount: number; cancelledCount: number } | null = null;
      let deactivationWarning: string | null = null;
      let attemptedDeactivation = false;
      let employeeBaseForSave = selectedEmployee;
      let regeneratedScheduleCache: Map<string, string> | undefined;
      let recurringBreakRegenerationResult: GenerateRecurringBreakBlockersResult | undefined;

      const nextSocials: Employee['socials'] = {};
      const instagramHandle = normalizeSocialHandle(instagram);
      const facebookLink = facebook.trim();
      const twitterHandle = normalizeSocialHandle(twitter);
      if (showEmployeeSocials) {
        if (instagramHandle) {
          nextSocials.instagram = instagramHandle;
        }
        if (facebookLink) {
          nextSocials.facebook = facebookLink;
        }
        if (twitterHandle) {
          nextSocials.twitter = twitterHandle;
        }
      }

      const updatedEmployee: Employee = {
        ...employeeBaseForSave,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: isResourceMode ? '' : normalizedEmail,
        phone: isResourceMode ? '' : phone.trim(),
        bio: showEmployeeBio
          ? (bio.trim().slice(0, EMPLOYEE_BIO_MAX_LENGTH) || undefined)
          : employeeBaseForSave.bio,
        socials: showEmployeeSocials ? nextSocials : employeeBaseForSave.socials,
        profilePictureImagePath: showEmployeeProfilePhoto
          ? profilePictureImagePath
          : employeeBaseForSave.profilePictureImagePath,
        admin: isSelectedEmployeeOwner ? true : (isOrchestratorMode ? employeeBaseForSave.admin : isAdmin),
        active,
        recurringBreaks: flattenRecurringBreaks(),
      };
      const hasAnySocial = showEmployeeSocials && Boolean(
        updatedEmployee.socials?.instagram || updatedEmployee.socials?.facebook || updatedEmployee.socials?.twitter
      );
      if (showEmployeeSocials && !hasAnySocial) {
        delete updatedEmployee.socials;
      }

      const nextRecurringBreaks = updatedEmployee.recurringBreaks ?? [];
      const previousRecurringBreaks = employeeBaseForSave.recurringBreaks ?? [];
      const recurringBreaksChanged = !areRecurringBreakSetsEqual(nextRecurringBreaks, previousRecurringBreaks);
      if (workHoursTemplateStatus === 'error' && recurringBreaksChanged) {
        throw new Error('Failed to load employee work hours. Please retry before saving schedule changes.');
      }
      if (isDeactivatingNow) {
        // Run deactivation as the final side-effect after non-destructive saves succeed.
        // This avoids appointment handoff/cancel side effects when earlier validation/persistence fails.
        updatedEmployee.active = employeeBaseForSave.active !== false;
      }

      await updateEmployee(
        employeeBaseForSave,
        updatedEmployee,
        showEmployeeProfilePhoto ? profilePictureFile : undefined
      );

      const existingWorkHours = employeeWorkHoursQuery.data;
      const hasExistingWorkHours = workHoursTemplateStatus === 'loaded' && !!existingWorkHours;
      const shouldBootstrapWorkHours = workHoursTemplateStatus === 'missing_template';
      const workHoursChanged = hasExistingWorkHours && !areWorkHoursEqual(workHours, existingWorkHours);
      const shouldPersistWorkHours = shouldBootstrapWorkHours || workHoursChanged;

      const shouldRegenerateSchedule = shouldPersistWorkHours || recurringBreaksChanged;

      if (shouldPersistWorkHours) {
        await addEmployeeWorkHours(selectedEmployee.id, workHours);
      }

      if (shouldRegenerateSchedule) {
        const scheduleDates = Array.from({ length: BUFFER_PERIOD * 2 + 1 }, (_, index) => dayjs().add(index, 'day'));
        const baseScheduleCache = queryClient.getQueryData<Map<string, string>>(['employeeSchedule', selectedEmployee.id]);
        const mutableScheduleCache = baseScheduleCache ? new Map(baseScheduleCache) : new Map<string, string>();
        const generatedScheduleUpdates = await updateEmployeeSchedulesForDates(selectedEmployee.id, workHours, scheduleDates);
        Object.entries(generatedScheduleUpdates).forEach(([dateKey, bitString]) => {
          mutableScheduleCache.set(dateKey, bitString);
        });

        const recurringBreakIds = Array.from(
          new Set([
            ...previousRecurringBreaks.map((definition) => definition.id),
            ...nextRecurringBreaks.map((definition) => definition.id),
          ].filter(Boolean))
        );
        recurringBreakRegenerationResult = await generateRecurringBreakBlockersForEmployee({
          employeeId: selectedEmployee.id,
          windowStart: dayjs(),
          windowEnd: dayjs().add(BUFFER_PERIOD * 2, 'day'),
          recurringBreakIdsToReset: recurringBreakIds,
          scheduleBitStrings: mutableScheduleCache,
        });
        regeneratedScheduleCache = mutableScheduleCache;
        updatedEmployee.appointments = recurringBreakRegenerationResult.updatedAppointmentsByDate;
      }

      const services = servicesQuery.data ?? [];
      const currentlyAssigned = new Set(
        services.filter((service) => service.employees.includes(employeeBaseForSave.id)).map((service) => service.id)
      );

      const toAdd = Array.from(selectedServiceIds).filter((id) => !currentlyAssigned.has(id));
      const toRemove = Array.from(currentlyAssigned).filter((id) => !selectedServiceIds.has(id));

      await Promise.all([
        ...toAdd.map((serviceId) =>
          updateDoc(
            doc(db, 'booking-websites', WEBSITE_ID, 'services', serviceId),
            { employees: arrayUnion(employeeBaseForSave.id) }
          )
        ),
        ...toRemove.map((serviceId) =>
          updateDoc(
            doc(db, 'booking-websites', WEBSITE_ID, 'services', serviceId),
            { employees: arrayRemove(employeeBaseForSave.id) }
          )
        ),
      ]);

      if (isDeactivatingNow) {
        attemptedDeactivation = true;
        try {
          deactivationResult = await executeEmployeeDeactivation({
            employeeId: selectedEmployee.id,
            strategy: input!.deactivationStrategy!,
            siteId: WEBSITE_ID,
          });
          // Reflect the known server-side deactivation outcome immediately.
          updatedEmployee.active = false;
          updatedEmployee.bookable = false;
        } catch (error) {
          const reason = (error as Error).message || 'Unknown error';
          deactivationWarning = `Profile and schedule changes were saved, but employee deactivation failed: ${reason}`;
          updatedEmployee.active = employeeBaseForSave.active !== false;
          updatedEmployee.bookable = employeeBaseForSave.bookable;
        }

        if (deactivationResult) {
          try {
            const refreshedEmployee = await fetchEmployeeById(selectedEmployee.id, WEBSITE_ID);
            if (!refreshedEmployee) {
              deactivationWarning = 'Employee was deactivated, but we could not refresh this profile. Please reopen this employee to verify.';
            } else {
              employeeBaseForSave = refreshedEmployee;
              updatedEmployee.appointments = refreshedEmployee.appointments;
              updatedEmployee.bookable = refreshedEmployee.bookable;
              updatedEmployee.active = refreshedEmployee.active;
            }
          } catch (error) {
            console.error('Failed to refresh employee after deactivation', error);
            deactivationWarning = 'Employee was deactivated, but profile refresh failed. Please reopen this employee to verify.';
          }
        }
      }

      return {
        updatedEmployee,
        toAdd,
        toRemove,
        didPersistWorkHours: shouldPersistWorkHours,
        savedWorkHours: shouldPersistWorkHours ? workHours : undefined,
        regeneratedScheduleCache,
        recurringBreakRegenerationResult,
        deactivationResult,
        deactivationWarning,
        attemptedDeactivation,
      };
    },
    onSuccess: ({
      updatedEmployee,
      toAdd,
      toRemove,
      didPersistWorkHours,
      savedWorkHours,
      regeneratedScheduleCache,
      recurringBreakRegenerationResult,
      deactivationResult,
      deactivationWarning,
      attemptedDeactivation,
    }) => {
      if (deactivationWarning) {
        setSaveSuccessDialogOpen(false);
        setResultSeverity('warning');
        setResultMessage(deactivationWarning);
      } else {
        const successMessage = deactivationResult
          ? `Employee updated and marked inactive. Reassigned ${deactivationResult.reassignedCount}, cancelled ${deactivationResult.cancelledCount} appointment(s).`
          : 'Employee updated successfully.';
        setSaveSuccessMessage(successMessage);
        setSaveSuccessDialogOpen(true);
        setResultMessage(null);
        setResultSeverity(null);
      }
      setOpenDialog(false);
      if (deactivationResult || attemptedDeactivation) {
        setDeactivateDialogOpen(false);
      }
      setSelectedEmployee(updatedEmployee);
      setDeactivatePreviewData(null);
      setDeactivatePreviewError(null);

      if (didPersistWorkHours && savedWorkHours) {
        queryClient.setQueryData<WorkHours>(
          ['manageEmployeeWorkHours', updatedEmployee.id],
          savedWorkHours
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ['manageEmployeeWorkHours', updatedEmployee.id] });
      }

      applyEmployeeSaveCache(queryClient, {
        updatedEmployee,
        siteId: WEBSITE_ID,
        regeneratedScheduleCache,
        recurringBreakRegenerationResult,
      });

      if (deactivationResult) {
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: ['appointment'] }),
          queryClient.invalidateQueries({ queryKey: ['hydrateAppointments'] }),
          queryClient.invalidateQueries({ queryKey: ['appointmentsByEmployee'] }),
          queryClient.invalidateQueries({ queryKey: ['employeeCalendarAppointments', WEBSITE_ID] }),
          queryClient.invalidateQueries({ queryKey: ['employeeSchedule'] }),
          queryClient.invalidateQueries({ queryKey: ['teamDayAppointments', WEBSITE_ID] }),
        ]).catch((validationError) => {
          console.error('Failed to validate caches after employee deactivation', validationError);
        });
      }

      if (toAdd.length > 0 || toRemove.length > 0) {
        queryClient.setQueryData<Service[]>(['services'], (previous = []) => (
          previous.map((service) => {
            if (!toAdd.includes(service.id) && !toRemove.includes(service.id)) {
              return service;
            }

            const employeeSet = new Set(service.employees ?? []);
            if (toAdd.includes(service.id)) {
              employeeSet.add(updatedEmployee.id);
            }
            if (toRemove.includes(service.id)) {
              employeeSet.delete(updatedEmployee.id);
            }

            return {
              ...service,
              employees: Array.from(employeeSet),
            };
          })
        ));
      }
      setNeedsRefreshOnDialogClose(false);
    },
    onError: async (error) => {
      if (isRecurringBreakPartialFailureError(error)) {
        let reconciled = false;
        try {
          await reconcileEmployeeCachesFromSource(queryClient, error.details.employeeId, WEBSITE_ID);
          reconciled = true;
        } catch (reconcileError) {
          console.error('Failed to reconcile employee caches after partial recurring-break failure', reconcileError);
        }
        if (reconciled) {
          setResultSeverity('warning');
          setResultMessage(
            "We couldn't finish applying all schedule updates, but we refreshed this employee's data. Please review the schedule and save again if needed."
          );
          setNeedsRefreshOnDialogClose(false);
        } else {
          setResultSeverity('error');
          setResultMessage(
            "We couldn't finish updating this employee's schedule, and automatic recovery failed. Please refresh the page and try again."
          );
          setNeedsRefreshOnDialogClose(true);
        }
        setDeactivatePreviewError(
          reconciled
            ? "Some schedule updates did not complete. We refreshed this employee's data."
            : "Schedule update and automatic recovery failed. Please refresh and retry."
        );
        return;
      }

      setResultSeverity('error');
      setResultMessage((error as Error).message || 'Failed to save employee updates.');
      setDeactivatePreviewError((error as Error).message || 'Failed to process deactivation.');
    },
  });

  const deletePreviewMutation = useMutation({
    mutationFn: async (input: { employeeId: string; requestId: number }) => {
      if (!input.employeeId) {
        throw new Error('No employee selected.');
      }
      return previewEmployeeDeletion(input.employeeId, WEBSITE_ID);
    },
    onSuccess: (preview, variables) => {
      if (variables.requestId !== deletePreviewRequestIdRef.current) {
        return;
      }
      setDeletePreviewData(preview);
      setDeletePreviewError(preview.guardrailError?.message || null);
      setDeleteDialogOpen(true);
    },
    onError: (error, variables) => {
      if (variables.requestId !== deletePreviewRequestIdRef.current) {
        return;
      }
      setDeletePreviewData(null);
      setDeletePreviewError((error as Error).message || 'Failed to prepare employee deletion.');
      setDeleteDialogOpen(true);
    },
  });

  const deactivationPreviewMutation = useMutation({
    mutationFn: async (input: { employeeId: string; requestId: number }) => {
      if (!input.employeeId) {
        throw new Error('No employee selected.');
      }
      return previewEmployeeDeactivation(input.employeeId, WEBSITE_ID);
    },
    onSuccess: (preview, variables) => {
      if (variables.requestId !== deactivatePreviewRequestIdRef.current) {
        return;
      }
      setDeactivatePreviewData(preview);
      setDeactivatePreviewError(null);
      setDeactivateDialogOpen(true);
    },
    onError: (error, variables) => {
      if (variables.requestId !== deactivatePreviewRequestIdRef.current) {
        return;
      }
      setDeactivatePreviewData(null);
      setDeactivatePreviewError((error as Error).message || 'Failed to prepare deactivation preview.');
      setDeactivateDialogOpen(true);
    },
  });

  const deleteEmployeeMutation = useMutation({
    mutationFn: async (cancelUnresolved: boolean) => {
      if (!deleteTargetEmployeeId) {
        throw new Error('No employee selected.');
      }
      return executeEmployeeDeletion({
        employeeId: deleteTargetEmployeeId,
        cancelUnresolved,
        siteId: WEBSITE_ID,
      });
    },
    onSuccess: (result) => {
      const employeeName = deleteTargetEmployeeName || 'Employee';
      const deletionSummary = `${employeeName} deleted. Reassigned ${result.reassignedCount}, cancelled ${result.cancelledCount}, removed ${result.deletedBlockersCount} blocker(s).`;
      const successMessage = result.authDisableFailed
        ? `${deletionSummary} Profile data was removed, but login could not be disabled. Disable this user in Firebase Auth manually.`
        : deletionSummary;
      setDeleteSuccessMessage(successMessage);
      setDeleteSuccessDialogOpen(true);
      setResultMessage(null);
      setResultSeverity(null);
      setDeleteDialogOpen(false);
      setDeleteConfirmInput('');
      setDeletePreviewData(null);
      setDeletePreviewError(null);
      setDeleteTargetEmployeeId(null);
      setDeleteTargetEmployeeName('');
      setNeedsRefreshOnDialogClose(false);
      setOpenDialog(false);
      setSelectedEmployee(null);
    },
    onError: (error) => {
      const deletionError = error as Error & { unresolvedAppointments?: Appointment[] };
      if (Array.isArray(deletionError.unresolvedAppointments) && deletionError.unresolvedAppointments.length > 0) {
        setDeletePreviewData((previous) => {
          if (!previous) {
            return previous;
          }
          return {
            ...previous,
            unresolvedAppointments: deletionError.unresolvedAppointments!,
          };
        });
      }
      setResultSeverity('error');
      setResultMessage(deletionError.message || `Failed to delete ${resourceSingular.toLowerCase()}.`);
      setDeletePreviewError(deletionError.message || `Failed to delete ${resourceSingular.toLowerCase()}.`);
    },
  });

  const isLoading = employeesQuery.isLoading || servicesQuery.isLoading || businessConfigQuery.isLoading;
  const isError = employeesQuery.isError || servicesQuery.isError || businessConfigQuery.isError;
  const isOrchestratorMode = businessConfigQuery.data?.preferences?.orchestratorMode === true;
  const bookingUxConfig = resolveBookingUxConfig(businessConfigQuery.data);
  const showEmployeeBio = bookingUxConfig.uiFlags.showEmployeeBio;
  const showEmployeeSocials = bookingUxConfig.uiFlags.showEmployeeSocials;
  const showEmployeeProfilePhoto = bookingUxConfig.uiFlags.showEmployeeProfilePhoto;
  const isResourceMode = bookingUxConfig.bookingModel === 'resource';
  const resourceSingular = bookingUxConfig.terminology.resourceSingular;
  const resourcePlural = bookingUxConfig.terminology.resourcePlural;
  const ownerId = businessConfigQuery.data?.paymentInfo?.owner || '';
  const primaryOrchestratorId = businessConfigQuery.data?.preferences?.primaryOrchestratorId || '';
  const isSelectedEmployeeOwner = selectedEmployee?.id === ownerId;
  const currentEmployeeId = currentEmployeeQuery.data?.id;
  const isSelectedEmployeeCurrentUser = selectedEmployee?.id === currentEmployeeId;

  const employees = employeesQuery.data ?? [];
  const services = servicesQuery.data ?? [];
  const sortedEmployees = useMemo(() => {
    const filteredEmployees = isResourceMode
      ? employees.filter((employee) => employee.id !== ownerId)
      : employees;
    if (!primaryOrchestratorId) {
      return filteredEmployees;
    }

    return [...filteredEmployees].sort((left, right) => {
      const leftIsPrimary = left.id === primaryOrchestratorId ? 1 : 0;
      const rightIsPrimary = right.id === primaryOrchestratorId ? 1 : 0;
      if (leftIsPrimary !== rightIsPrimary) {
        return rightIsPrimary - leftIsPrimary;
      }
      return `${left.firstName} ${left.lastName}`.localeCompare(`${right.firstName} ${right.lastName}`);
    });
  }, [employees, isResourceMode, ownerId, primaryOrchestratorId]);

  const dialogTitle = selectedEmployee
    ? `Manage ${selectedEmployee.firstName} ${selectedEmployee.lastName}`
    : `Manage ${resourceSingular}`;
  const deleteConfirmationTarget = deleteTargetEmployeeName;
  const deleteHasUnresolved = (deletePreviewData?.unresolvedAppointments.length ?? 0) > 0;
  const canConfirmDelete = deleteConfirmInput.trim() === deleteConfirmationTarget;
  const deactivationReassignableCount = deactivatePreviewData?.reassignments.length ?? 0;
  const deactivationUnresolvedCount = deactivatePreviewData?.unresolvedAppointments.length ?? 0;
  const canHandoffAll = deactivationUnresolvedCount === 0;
  const fullNameValue = `${firstName} ${lastName}`.trim();

  const handleOpenDeleteDialog = () => {
    if (!selectedEmployee || isSelectedEmployeeCurrentUser) {
      return;
    }
    const nextRequestId = deletePreviewRequestIdRef.current + 1;
    deletePreviewRequestIdRef.current = nextRequestId;
    const fullName = `${selectedEmployee.firstName} ${selectedEmployee.lastName}`.trim();
    setDeleteTargetEmployeeId(selectedEmployee.id);
    setDeleteTargetEmployeeName(fullName);
    setDeleteDialogOpen(true);
    setDeleteConfirmInput('');
    setDeletePreviewData(null);
    setDeletePreviewError(null);
    deletePreviewMutation.mutate({
      employeeId: selectedEmployee.id,
      requestId: nextRequestId,
    });
  };

  const handleCloseDeleteDialog = () => {
    deletePreviewRequestIdRef.current += 1;
    setDeleteDialogOpen(false);
    setDeleteConfirmInput('');
    setDeletePreviewData(null);
    setDeletePreviewError(null);
    setDeleteTargetEmployeeId(null);
    setDeleteTargetEmployeeName('');
  };

  const handleOpenDeactivationDialog = () => {
    if (!selectedEmployee) {
      return;
    }
    const nextRequestId = deactivatePreviewRequestIdRef.current + 1;
    deactivatePreviewRequestIdRef.current = nextRequestId;
    setDeactivateDialogOpen(true);
    setDeactivatePreviewData(null);
    setDeactivatePreviewError(null);
    deactivationPreviewMutation.mutate({
      employeeId: selectedEmployee.id,
      requestId: nextRequestId,
    });
  };

  const handleCloseDeactivationDialog = () => {
    deactivatePreviewRequestIdRef.current += 1;
    setDeactivateDialogOpen(false);
    setDeactivatePreviewData(null);
    setDeactivatePreviewError(null);
  };

  const handleCloseDeleteSuccessDialog = () => {
    setDeleteSuccessDialogOpen(false);
    window.location.reload();
  };

  const refreshAfterRecoveryFailure = (employeeId: string) => {
    void Promise.all([
      queryClient.invalidateQueries({ queryKey: ['allEmployees'] }),
      queryClient.invalidateQueries({ queryKey: ['employees'] }),
      queryClient.invalidateQueries({ queryKey: ['services'] }),
      queryClient.invalidateQueries({ queryKey: ['manageEmployeeWorkHours', employeeId] }),
      queryClient.invalidateQueries({ queryKey: ['employeeSchedule', employeeId] }),
      queryClient.invalidateQueries({ queryKey: ['appointmentsByEmployee', employeeId] }),
      queryClient.invalidateQueries({ queryKey: ['employeeCalendarAppointments', WEBSITE_ID, employeeId] }),
      queryClient.invalidateQueries({ queryKey: ['hydrateAppointments'] }),
      queryClient.invalidateQueries({ queryKey: ['appointment'] }),
    ]).catch((refreshError) => {
      console.error('Failed to refresh employee caches after recovery failure', refreshError);
    });
  };

  const handleCloseManageDialog = () => {
    const employeeIdToRefresh = selectedEmployee?.id ?? previousDialogEmployeeIdRef.current;
    setOpenDialog(false);
    if (!needsRefreshOnDialogClose) {
      return;
    }
    setNeedsRefreshOnDialogClose(false);
    if (employeeIdToRefresh) {
      refreshAfterRecoveryFailure(employeeIdToRefresh);
    }
  };

  const handleSaveAttempt = () => {
    if (!selectedEmployee) {
      return;
    }
    setSaveSuccessDialogOpen(false);
    const deactivating = selectedEmployee.active !== false && active === false;
    if (deactivating) {
      handleOpenDeactivationDialog();
      return;
    }
    saveMutation.mutate(undefined);
  };

  useEffect(() => {
    if (!deleteEmployeeMutation.isPending) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [deleteEmployeeMutation.isPending]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <Alert severity="error">{`Failed to load ${resourcePlural.toLowerCase()} data.`}</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%', pt: { xs: 1, sm: 2 } }}>
      <Backdrop
        open={deleteEmployeeMutation.isPending}
        sx={{
          color: '#fff',
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 2,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">{`Deleting ${resourceSingular.toLowerCase()}...`}</Typography>
        <Typography variant="body2">Please do not navigate away.</Typography>
      </Backdrop>
      <Backdrop
        open={saveMutation.isPending}
        sx={{
          color: '#fff',
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 2,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">{`Saving ${resourceSingular.toLowerCase()} updates...`}</Typography>
        <Typography variant="body2">Please wait.</Typography>
      </Backdrop>
      {resultMessage && resultSeverity && (
        <Alert severity={resultSeverity} sx={{ mb: 2 }}>
          {resultMessage}
        </Alert>
      )}
      <Stack spacing={2}>
        {sortedEmployees.map((employee) => (
          <Card
            key={employee.id}
            variant="outlined"
            sx={{
              width: '100%',
              mx: 'auto',
            }}
          >
            {isMobile ? (
              <CardActionArea
                onClick={() => {
                  setSelectedEmployee(employee);
                  setOpenDialog(true);
                }}
              >
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {employee.firstName} {employee.lastName}
                      </Typography>
                      {!isResourceMode && (
                        <Typography variant="body2" color="text.secondary">
                          {employee.email || 'No email'}{employee.phone ? ` • ${employee.phone}` : ''}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                        {employee.id === primaryOrchestratorId && (
                          <StarIcon
                            color="warning"
                            fontSize="small"
                            titleAccess="Primary orchestrator"
                          />
                        )}
                        {employee.admin && <Chip size="small" label="Admin" />}
                        {employee.active !== false ? <Chip size="small" label="Active" color="success" /> : <Chip size="small" label="Inactive" color="default" />}
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </CardActionArea>
            ) : (
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {employee.firstName} {employee.lastName}
                    </Typography>
                    {!isResourceMode && (
                      <Typography variant="body2" color="text.secondary">
                        {employee.email || 'No email'}{employee.phone ? ` • ${employee.phone}` : ''}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                      {employee.id === primaryOrchestratorId && (
                        <StarIcon
                          color="warning"
                          fontSize="small"
                          titleAccess="Primary orchestrator"
                        />
                      )}
                      {employee.admin && <Chip size="small" label="Admin" />}
                      {employee.active !== false ? <Chip size="small" label="Active" color="success" /> : <Chip size="small" label="Inactive" color="default" />}
                    </Stack>
                  </Box>
                  <Box sx={{ flexShrink: 0 }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        setSelectedEmployee(employee);
                        setOpenDialog(true);
                      }}
                    >
                      Manage
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            )}
          </Card>
        ))}
      </Stack>
      <Portal>
        <Fab
          color="secondary"
          aria-label="Add employee"
          onClick={() => navigate('/admin/add-employee')}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: (muiTheme) => muiTheme.zIndex.drawer + 2,
          }}
        >
          <AddIcon />
        </Fab>
      </Portal>

      <Dialog
        open={openDialog}
        onClose={handleCloseManageDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pr: 6 }}>
          {dialogTitle}
          <IconButton
            color="secondary"
            onClick={handleCloseManageDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedEmployee && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Accordion
                expanded={expandedManageSection === 'profile'}
                onChange={(_, expanded) => setExpandedManageSection(expanded ? 'profile' : null)}
                disableGutters
                elevation={0}
                square
                sx={{
                  width: '100%',
                  border: 'none',
                  boxShadow: 'none',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 2,
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    borderBottom: expandedManageSection === 'profile' ? '1px solid' : 'none',
                    borderColor: 'divider',
                    '& .MuiAccordionSummary-content': { my: 1 },
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Profile</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 1, pb: 2 }}>
                  <Stack spacing={2}>
                    {showEmployeeProfilePhoto && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>
                          Profile Picture (Optional)
                        </Typography>
                        <ImageSelect
                          initialImagePath={selectedEmployee.profilePictureImagePath}
                          onImageChange={handleProfilePictureChange}
                          allowDelete
                          styleType="circle"
                          altText={`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
                        />
                      </Box>
                    )}
                    {showEmployeeBio && (
                      <TextField
                        label="Bio (Optional)"
                        fullWidth
                        multiline
                        minRows={3}
                        value={bio}
                        onChange={(event) => setBio(event.target.value.slice(0, EMPLOYEE_BIO_MAX_LENGTH))}
                        placeholder="Add a short bio for the homepage staff section."
                        helperText={`${bio.length}/${EMPLOYEE_BIO_MAX_LENGTH}`}
                        inputProps={{ maxLength: EMPLOYEE_BIO_MAX_LENGTH }}
                      />
                    )}
                    {showEmployeeSocials && (
                      <>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <TextField
                            label="Instagram Username (Optional)"
                            fullWidth
                            value={instagram}
                            onChange={(event) => setInstagram(event.target.value)}
                            placeholder="username"
                            helperText="Enter username only (without @)."
                          />
                          <TextField
                            label="Facebook URL (Optional)"
                            fullWidth
                            value={facebook}
                            onChange={(event) => setFacebook(event.target.value)}
                            placeholder="https://facebook.com/username"
                          />
                        </Stack>
                        <TextField
                          label="Twitter / X Username (Optional)"
                          fullWidth
                          value={twitter}
                          onChange={(event) => setTwitter(event.target.value)}
                          placeholder="username"
                          helperText="Enter username only (without @)."
                        />
                      </>
                    )}
                    {isResourceMode ? (
                      <TextField
                        label="Name"
                        fullWidth
                        value={fullNameValue}
                        onChange={(event) => {
                          const parsed = parseFullName(event.target.value);
                          setFirstName(parsed.firstName);
                          setLastName(parsed.lastName);
                        }}
                      />
                    ) : (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                          label="First Name"
                          fullWidth
                          value={firstName}
                          onChange={(event) => setFirstName(event.target.value)}
                        />
                        <TextField
                          label="Last Name"
                          fullWidth
                          value={lastName}
                          onChange={(event) => setLastName(event.target.value)}
                        />
                      </Stack>
                    )}
                    {!isResourceMode && (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                          label="Email"
                          fullWidth
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          disabled={selectedEmployee.isOrchestrated !== true}
                          helperText={selectedEmployee.isOrchestrated !== true ? 'Email is locked for login-enabled staff.' : undefined}
                        />
                        <TextField
                          label="Phone"
                          fullWidth
                          value={phone}
                          onChange={(event) => setPhone(event.target.value)}
                        />
                      </Stack>
                    )}
                    {!isResourceMode && (
                      <FormControlLabel
                        control={(
                          <Checkbox
                            checked={isSelectedEmployeeOwner ? true : isAdmin}
                            onChange={(event) => setIsAdmin(event.target.checked)}
                          />
                        )}
                        label="Admin"
                        disabled={isOrchestratorMode || isSelectedEmployeeOwner}
                      />
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedManageSection === 'services'}
                onChange={(_, expanded) => setExpandedManageSection(expanded ? 'services' : null)}
                disableGutters
                elevation={0}
                square
                sx={{
                  width: '100%',
                  border: 'none',
                  boxShadow: 'none',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 2,
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    borderBottom: expandedManageSection === 'services' ? '1px solid' : 'none',
                    borderColor: 'divider',
                    '& .MuiAccordionSummary-content': { my: 1 },
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Services</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 1, pb: 2 }}>
                  <FormGroup>
                    {services.map((service) => (
                      <FormControlLabel
                        key={service.id}
                        control={(
                          <Checkbox
                            checked={selectedServiceIds.has(service.id)}
                            onChange={() => toggleService(service.id)}
                          />
                        )}
                        label={`${service.title} (${service.time} mins)`}
                      />
                    ))}
                  </FormGroup>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedManageSection === 'work_hours'}
                onChange={(_, expanded) => setExpandedManageSection(expanded ? 'work_hours' : null)}
                disableGutters
                elevation={0}
                square
                sx={{
                  width: '100%',
                  border: 'none',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 2,
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    borderBottom: expandedManageSection === 'work_hours' ? '1px solid' : 'none',
                    borderColor: 'divider',
                    '& .MuiAccordionSummary-content': { my: 1 },
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Work Hours</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 1, pb: 2 }}>
                  <Stack spacing={1}>
                    {workHoursTemplateStatus === 'error' && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        Unable to load work hours right now. You can still save profile and service changes.
                      </Alert>
                    )}
                    {workHoursTemplateStatus !== 'error' && DAYS_OF_WEEK_PREFERRED_ORDER.map((day) => {
                      const parsed = extractTimesFromBitString(workHours[day]);
                      return (
                        <ScheduleCard
                          key={day}
                          day={day}
                          initialWorkHours={parsed}
                          onScheduleChange={handleScheduleChange}
                          recurringBreaks={recurringBreakTemplates[day]}
                          onRecurringBreaksChange={handleRecurringBreaksChange}
                          templateEndDate={templateEndDate}
                        />
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <FormControlLabel
                control={<Checkbox checked={active} onChange={(event) => setActive(event.target.checked)} />}
                label="Active"
              />

              {resultMessage && resultSeverity && (
                <Alert severity={resultSeverity}>
                  {resultMessage}
                </Alert>
              )}

              <Box
                sx={{
                  mt: 1,
                  pt: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Button
                  color="error"
                  variant="text"
                  onClick={handleOpenDeleteDialog}
                  disabled={
                    saveMutation.isPending ||
                    deletePreviewMutation.isPending ||
                    deleteEmployeeMutation.isPending ||
                    !selectedEmployee ||
                    isSelectedEmployeeCurrentUser
                  }
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {`Delete ${resourceSingular}`}
                </Button>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 2,
            py: 1,
            gap: 0,
            justifyContent: 'space-between',
            '& > :not(:first-of-type)': { ml: 0 },
            '& .MuiButton-root': { m: 0 },
          }}
        >
          <Button
            onClick={handleCloseManageDialog}
            sx={{ flex: 1, borderRadius: '4px', py: 1.2, ml: 0 }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAttempt}
            disabled={
              saveMutation.isPending ||
              deleteEmployeeMutation.isPending ||
              deactivationPreviewMutation.isPending ||
              !selectedEmployee ||
              isWorkHoursLoadingForSelection
            }
            sx={{ flex: 1, borderRadius: '4px', py: 1.2, ml: 0 }}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={deleteEmployeeMutation.isPending ? undefined : handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pr: 6 }}>
          {`Delete ${resourceSingular}`}
          <IconButton
            color="secondary"
            onClick={handleCloseDeleteDialog}
            disabled={deleteEmployeeMutation.isPending}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {deletePreviewMutation.isPending ? (
            <Typography variant="body2" color="text.secondary">Preparing deletion preview...</Typography>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="warning">
                {`This permanently removes the ${resourceSingular.toLowerCase()} profile and related records.`}
              </Alert>

              {deletePreviewError && (
                <Alert severity="error">{deletePreviewError}</Alert>
              )}

              {deletePreviewData && !deletePreviewData.guardrailError && (
                <>
                  <DialogContentText>
                    Review the impact before deleting <strong>{deleteConfirmationTarget}</strong>.
                  </DialogContentText>

                  <Chip
                    color="info"
                    label={`${deletePreviewData.reassignments.length} upcoming appointment(s) will be reassigned`}
                  />

                  <TextField
                    label={`Type "${deleteConfirmationTarget}" to confirm`}
                    value={deleteConfirmInput}
                    onChange={(event) => setDeleteConfirmInput(event.target.value)}
                    fullWidth
                  />
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteEmployeeMutation.isPending}>
            Close
          </Button>
          {deletePreviewData && !deletePreviewData.guardrailError && (
            <Button
              variant="contained"
              color="error"
              disabled={deleteEmployeeMutation.isPending || !canConfirmDelete}
              onClick={() => deleteEmployeeMutation.mutate(deleteHasUnresolved)}
            >
              {deleteEmployeeMutation.isPending
                ? 'Deleting...'
                : deleteHasUnresolved
                  ? 'Cancel Unresolved & Delete'
                  : `Delete ${resourceSingular}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={deactivateDialogOpen}
        onClose={saveMutation.isPending ? undefined : handleCloseDeactivationDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pr: 6 }}>
          {`Mark ${resourceSingular} Inactive`}
          <IconButton
            color="secondary"
            onClick={handleCloseDeactivationDialog}
            disabled={saveMutation.isPending}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {deactivationPreviewMutation.isPending ? (
            <Typography variant="body2" color="text.secondary">Preparing appointment impact...</Typography>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <DialogContentText>
                This employee will be marked as inactive and removed from future booking availability.
              </DialogContentText>

              {deactivatePreviewError && (
                <Alert severity="error">{deactivatePreviewError}</Alert>
              )}

              {deactivatePreviewData && (
                <>
                  <Chip
                    color="info"
                    label={`${deactivationReassignableCount} upcoming appointment(s) can be handed off`}
                  />
                  <Chip
                    color={canHandoffAll ? 'success' : 'warning'}
                    label={`${deactivationUnresolvedCount} upcoming appointment(s) cannot be handed off automatically`}
                  />
                  {!canHandoffAll && (
                    <Alert severity="warning">
                      Hand off requires all upcoming appointments to be transferable.
                    </Alert>
                  )}
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeactivationDialog} disabled={saveMutation.isPending}>
            Close
          </Button>
          <Button
            variant="outlined"
            disabled={
              saveMutation.isPending ||
              deactivationPreviewMutation.isPending ||
              !deactivatePreviewData ||
              !canHandoffAll
            }
            onClick={() => saveMutation.mutate({ deactivationStrategy: 'handoff' })}
          >
            {saveMutation.isPending ? 'Applying...' : 'Hand Off Appointments'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={saveMutation.isPending || deactivationPreviewMutation.isPending || !deactivatePreviewData}
            onClick={() => saveMutation.mutate({ deactivationStrategy: 'cancel' })}
          >
            {saveMutation.isPending ? 'Applying...' : 'Cancel Appointments'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={saveSuccessDialogOpen}
        onClose={() => setSaveSuccessDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <DialogContentText>{saveSuccessMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setSaveSuccessDialogOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteSuccessDialogOpen}
        onClose={handleCloseDeleteSuccessDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{`${resourceSingular} Deleted`}</DialogTitle>
        <DialogContent>
          <DialogContentText>{deleteSuccessMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseDeleteSuccessDialog}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageEmployeesPage;
